import { sessionStore } from '@shared/session/store';
import { handleSubscriptionChange, registerServiceWorker, releaseDevice, syncPushSubscription } from './pushClient';

export interface WebPushOptions {
  /** SPA navigation for notification clicks (keeps the in-memory session). */
  navigate: (url: string) => void;
  /** A push arrived while a page is open (refresh the bell). */
  onPushReceived?: () => void;
}

interface WorkerMessage {
  type?: string;
  url?: string;
  oldEndpoint?: string | null;
}

let started = false;

/**
 * App start (production and dev): register /sw.js, follow the session to keep this browser's subscription in step
 * with the signed-in account, and act on the worker's messages. No-op where service workers are unavailable.
 */
export function startWebPush(options: WebPushOptions): void {
  if (started || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  started = true;

  const register = () => void registerServiceWorker();
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
    const data = event.data ?? {};
    const userId = sessionStore.get().user?.id;
    if (data.type === 'tibyan:navigate' && typeof data.url === 'string' && data.url.startsWith('/')) options.navigate(data.url);
    else if (data.type === 'tibyan:push-received') options.onPushReceived?.();
    else if (data.type === 'tibyan:push-subscription-changed' && userId) void handleSubscriptionChange(userId, data.oldEndpoint ?? null).catch(() => undefined);
  });
  navigator.serviceWorker.startMessages?.();

  let previous = sessionStore.get();
  sessionStore.subscribe((state) => {
    const before = previous;
    previous = state;
    const userId = state.status === 'authenticated' ? state.user?.id : undefined;
    const beforeId = before.status === 'authenticated' ? before.user?.id : undefined;
    if (userId && userId !== beforeId) void syncPushSubscription(userId).catch(() => undefined);
    if (beforeId && beforeId !== userId) void releaseDevice(beforeId);
  });
}
